import { createSelector } from '@reduxjs/toolkit';
import { WEEK_IN_DAYS } from 'core/common/constants';
import { RootInitialState } from 'core/common/store';
import { Condition } from 'core/flow/entities';
import {
  getDefaultOffers,
  getDiscountOffer,
  getDiscountOfferNextPeriod,
  getDiscountUpsells,
  getSelectedOffer,
  getSelectedUpsell,
  getTimeUpOffer,
  getTimeUpOfferSamePeriod,
  getSelectedTrialOffer,
  getUpsells,
  getUserCurrency,
} from 'core/offers/store/selectors';
import { getScan } from 'core/scan/store/selectors';
import {
  getEmail,
  getUserInformation,
  getUserName,
  getUserResponses,
} from 'core/user/store/selectors';

import { getBaseConditionAttributes } from './baseConditionAttributes';

const lowestOfferPrice = 1;

export const getFunnelConfig = (state: RootInitialState) => {
  return state.funnelConfig;
};

export const getFunnelFlow = (state: RootInitialState) => {
  return state.funnelConfig.flow;
};

export const getFunnelName = (state: RootInitialState) => {
  const funnelConfig = getFunnelConfig(state).config;

  if (funnelConfig) {
    return funnelConfig.name;
  }

  return '';
};

export const getEmailFlowAttributesReports = (state: RootInitialState) => {
  const flow = getFunnelFlow(state);
  const funnelConfig = getFunnelConfig(state).config;

  const emailFlowAttributes =
    flow?.attributes?.emailFlowAttributes ?? funnelConfig?.attributes?.emailFlowAttributes;

  return emailFlowAttributes?.reports ?? {};
};

export const getOfferPriceDifference = createSelector(
  [getSelectedOffer, getTimeUpOfferSamePeriod],
  (selectedOffer, timeUpOffer) => {
    if (!selectedOffer || !timeUpOffer) {
      return { price: 0, trialPrice: 0 };
    }

    return {
      price: Math.round(timeUpOffer.price - selectedOffer.price),
      trialPrice: Math.round(timeUpOffer.trialPrice - selectedOffer.trialPrice),
    };
  },
);

export const getOfferPriceDifferencePercent = createSelector(
  [getOfferPriceDifference, getTimeUpOfferSamePeriod],
  (priceDifference, timeUpOffer) => {
    return {
      price: timeUpOffer?.price ? Math.round((priceDifference.price / timeUpOffer.price) * 100) : 0,
      trialPrice: timeUpOffer?.trialPrice
        ? Math.round((priceDifference.trialPrice / timeUpOffer.trialPrice) * 100)
        : 0,
    };
  },
);

export const getOfferPeriodWeeks = createSelector([getSelectedOffer], (selectedOffer) => {
  const periodDays = Number(selectedOffer?.period.split(' ')[0] ?? '');
  const trialPeriodDays = Number(selectedOffer?.trialPeriod?.split(' ')[0] ?? '');

  return {
    period: periodDays ? Math.round(periodDays / WEEK_IN_DAYS) : 0,
    trialPeriod: trialPeriodDays ? Math.round(trialPeriodDays / WEEK_IN_DAYS) : 0,
  };
});

const getOfferConditionAttributes = createSelector(
  [
    getSelectedOffer,
    getDefaultOffers,
    getUserCurrency,
    getDiscountOffer,
    getTimeUpOffer,
    getSelectedUpsell,
    getSelectedTrialOffer,
    getOfferPriceDifference,
    getOfferPriceDifferencePercent,
    getOfferPeriodWeeks,
    getTimeUpOfferSamePeriod,
    getDiscountOfferNextPeriod,
    getUpsells,
    getDiscountUpsells,
  ],
  (
    selectedOffer,
    defaultOffers,
    currencyData,
    discountOffer,
    timeUpOffer,
    selectedUpsell,
    selectedTrialOffer,
    offerPriceDifference,
    offerPriceDifferencePercent,
    offerPeriodWeeks,
    timeUpOfferSamePeriod,
    discountOfferNextPeriod,
    upsells,
    discountUpsells,
  ) => {
    const selectedOfferPeriod = selectedOffer?.period.split(' ')[0] ?? '';
    const selectedOfferTrialPeriod = selectedOffer?.trialPeriod?.split(' ')[0] ?? '';
    const highestTrialPrice = !!defaultOffers.length
      ? defaultOffers[defaultOffers.length - 1].trialPriceWithCurrency
      : '';
    const trialPricesDifference = !!defaultOffers.length
      ? defaultOffers[defaultOffers.length - 1].trialPrice - lowestOfferPrice
      : lowestOfferPrice;

    return {
      currency: currencyData.currency,
      currencySymbol: currencyData.currencySymbol,
      highestTrialPrice,
      selectedOfferPeriod,
      selectedOfferTrialPeriod,
      selectedOfferName: selectedOffer?.offerId ?? '',
      selectedOfferTrialPrice: selectedOffer?.trialPrice ?? lowestOfferPrice,
      selectedOfferTrialPriceWithCurrency: selectedOffer?.trialPriceWithCurrency ?? '',
      selectedOfferPrice: selectedOffer?.price ?? lowestOfferPrice,
      selectedOfferPriceWithCurrency: selectedOffer?.priceWithCurrency ?? '',
      selectedTrialOfferPriceWithCurrency: selectedTrialOffer?.priceWithCurrency ?? '',
      // offer was selected by user - trial price
      selectedOfferTrialPriceWithCurrencySymbol: selectedOffer?.trialPriceWithCurrency ?? '',
      // offer was selected by user - full price
      selectedOfferPriceWithCurrencySymbol: selectedOffer?.priceWithCurrency ?? '',
      // time up offer - full price
      fullPriceDiscountOfferWithCurrencySymbol: timeUpOffer?.priceWithCurrency ?? '',
      // discount offer - trial price
      discountOfferTrialPriceWithCurrencySymbol: discountOffer?.trialPriceWithCurrency ?? '',
      // discount offer - full price
      discountOfferPriceWithCurrencySymbol: discountOffer?.priceWithCurrency ?? '',
      consultationUpsellOfferPriceWithCurrencySymbol: upsells.CONSULTATION?.priceWithCurrency ?? '',
      consultationUpsellOfferTrialPriceWithCurrencySymbol:
        upsells.CONSULTATION?.trialPriceWithCurrency ?? '',
      discountConsultationUpsellOfferPriceWithCurrencySymbol:
        discountUpsells.CONSULTATION?.priceWithCurrency ?? '',
      discountConsultationUpsellOfferTrialPriceWithCurrencySymbol:
        discountUpsells.CONSULTATION?.trialPriceWithCurrency ?? '',
      upsells,
      discountUpsells,
      selectedUpsellName: selectedUpsell?.title ?? '',
      selectedUpsellPriceWithCurrencySymbol: selectedUpsell?.price ?? '',
      selectedUpsellOldPriceWithCurrencySymbol: selectedUpsell?.oldPrice ?? '',
      selectedTrialOfferTrialPeriod: selectedTrialOffer?.trialPeriod?.split(' ')[0] ?? '',
      trialPricesDifference,
      offerPriceDifference: offerPriceDifference.price.toFixed(2),
      offerTrialPriceDifference: offerPriceDifference.trialPrice.toFixed(2),
      offerPriceDifferencePercent: offerPriceDifferencePercent.price,
      offerTrialPriceDifferencePercent: offerPriceDifferencePercent.trialPrice,
      selectedOfferPeriodWeeks: offerPeriodWeeks.period,
      selectedOfferTrialPeriodWeeks: offerPeriodWeeks.trialPeriod,
      timeUpOfferSamePeriodPriceWithCurrencySymbol: timeUpOfferSamePeriod?.priceWithCurrency ?? '',
      timeUpOfferSamePeriodTrialPriceWithCurrencySymbol:
        timeUpOfferSamePeriod?.trialPriceWithCurrency ?? '',
      discountOfferNextPeriodPrice: discountOfferNextPeriod?.priceWithCurrency ?? '',
      discountOfferNextPeriodTrialPrice: discountOfferNextPeriod?.trialPriceWithCurrency ?? '',
    };
  },
);

export const getConditionAttributes = createSelector(
  [getBaseConditionAttributes, getOfferConditionAttributes],
  (baseAttributes, offerAttributes) => {
    return { ...baseAttributes, ...offerAttributes };
  },
);

export type ConditionAttributes = ReturnType<typeof getConditionAttributes>;

export type ConditionDefinition = Partial<Record<keyof ConditionAttributes, Condition>>;

export type FeatureDefinition = Partial<Record<string, Condition>>;

export const getQuizData = createSelector(
  [getUserInformation, getEmail, getUserResponses, getUserName, getScan],
  (userInformation, email, userResponses, name, scan) => {
    const userResponsesValues = Object.values(userResponses);

    return {
      email,
      name,
      birth_time: userInformation.time || '12:00:00',
      gender: userInformation.gender,
      birth_date: userInformation.date || '1990-01-01',
      partnerName: userInformation.partnerName,
      partnerTime: userInformation.partnerTime,
      partner_gender: userInformation.partnerGender,
      partner_ideal_age: userInformation.partnerIdealAge,
      partner_birth_date: userInformation.partnerDate,
      partner_birth_place: userInformation.partnerLocation,
      flow_choice: userInformation.relationshipType,
      flow_goal:
        userInformation.palmReadingGoal ||
        userInformation.wpGoal ||
        userInformation.relationshipGoal ||
        userInformation.mainGoal,
      wpGoal: userInformation.wpGoal,
      relationship: userInformation.relationship,
      mainPain: userInformation.mainPain,
      mainGoal: userInformation.mainGoal,
      challenges: userInformation.challenges,
      topics: userInformation.topics,
      relationshipGoal: userInformation.relationshipGoal,
      palmReadingGoal: userInformation.palmReadingGoal,
      astrology_knowledge: userInformation.astrologyKnowledge,
      traits_relate_to_statement: userInformation.relateToStatement,
      traits_most_important: userInformation.mostImportant,
      relationshipStatus: userInformation.relationshipStatus,
      relationship_not_good_enough: userInformation.goodEnough,
      relationship_ideal_date: userInformation.idealDate,
      relationship_future_partner: userInformation.futurePartner,
      relationship_ideal_partner: userInformation.idealPartner,
      relationship_how_to_make_decisions: userInformation.decisions,
      scan: {
        photoId: scan.photoId,
      },
      ancestor_data: userInformation.ancestor_data,
      nature_data: userInformation.nature_data,
      tradition_data: userInformation.tradition_data,
      crowdComfort_data: userInformation.crowdComfort_data,
      intuitiveHealing_data: userInformation.intuitiveHealing_data,
      moonPhase_data: userInformation.moonPhase_data,
      worryPartnerLeaves: userInformation.worryPartnerLeaves,
      archetype: userInformation.archetype,
      partnerZodiac: userInformation.partnerZodiac,
      partnerGender: userInformation.partnerGender,
      holdingBackReason: userInformation.holdingBackReason,
      ethnicBackground: userInformation.ethnicBackground,
      manifestHasPerson: userInformation.manifestHasPerson,
      loveLanguage: userInformation.loveLanguage,
      partnerSimilarityPreference: userInformation.partnerSimilarityPreference,
      relationshipFear: userInformation.relationshipFear,
      feelEmpowered: userInformation.feelEmpowered,
      peacePlaces: userInformation.peacePlaces,
      animalsConnection: userInformation.animalsConnection,
      elementAffinity: userInformation.elementAffinity,
      emotionExpress: userInformation.emotionExpress,
      effectOnOthers: userInformation.effectOnOthers,
      relationshipRoleRepair: userInformation.relationshipRoleRepair,
      practices: userInformation.practices,
      colorPreference: userInformation.colorPreference,
      respondWhenHurt: userInformation.respondWhenHurt,
      periodPreference: userInformation.periodPreference,
      idealRelationshipDynamic: userInformation.idealRelationshipDynamic,
      responses: userResponsesValues,
    };
  },
);
